import { useMemo, useState } from 'react';
import { ArrowLeft, FilePlus2, FileUp, Users, FileText, Search } from 'lucide-react';
import W2CreatorModal from '../W2CreatorModal';
import { W2PartyForm, EMPTY_PARTY, partyFromW2, partyFromClient } from './W2PartyForm';
import { W2UploadPanel } from './W2UploadPanel';
import { SavedW2List } from './SavedW2List';

const SOURCES = [
  { id: 'scratch', icon: FilePlus2, title: 'Start from scratch', desc: 'Blank W-2 — type every box, auto-math included.' },
  { id: 'upload', icon: FileUp, title: 'Upload a prior W-2', desc: 'AI reads last year\u2019s W-2, then you pick the new year.' },
  { id: 'client', icon: Users, title: 'From a saved client', desc: 'Pre-fill from a Payday ST client and their paystub.' },
];

const SourceTile = ({ s, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`w2studio-source-${s.id}`}
    className={`text-left p-4 rounded-xl border-2 transition-colors ${active ? 'border-emerald-500 bg-emerald-500/10' : 'border-zinc-800 bg-zinc-900 hover:border-zinc-600'}`}>
    <s.icon className="w-5 h-5 text-emerald-400 mb-2" />
    <p className="text-white font-semibold">{s.title}</p>
    <p className="text-xs text-zinc-400 mt-1">{s.desc}</p>
  </button>
);

const ClientPicker = ({ clients, onPick }) => {
  const [q, setQ] = useState('');
  const shown = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-3" data-testid="w2studio-client-picker">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" data-testid="w2studio-client-search"
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:outline-none focus:border-emerald-500" />
      </div>
      <div className="max-h-64 overflow-y-auto space-y-1.5">
        {shown.map((c) => (
          <button key={c.id} type="button" onClick={() => onPick(c)} data-testid={`w2studio-client-${c.id}`}
            className="w-full text-left px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 hover:border-emerald-500 text-sm text-white">
            {c.name}
          </button>
        ))}
        {!shown.length && <p className="text-xs text-zinc-500">No saved clients found.</p>}
      </div>
    </div>
  );
};

export default function W2Studio({ savedClients = [], onBack }) {
  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 7 }, (_, i) => currentYear - i), [currentYear]);
  const [source, setSource] = useState(null);
  const [party, setParty] = useState(EMPTY_PARTY);
  const [seed, setSeed] = useState(null);
  const [sourceYear, setSourceYear] = useState(null);
  const [targetYear, setTargetYear] = useState(currentYear);
  const [client, setClient] = useState(null);
  const [ready, setReady] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);

  const pickSource = (id) => {
    setSource(id); setSeed(null); setSourceYear(null); setClient(null); setParty(EMPTY_PARTY);
    setTargetYear(currentYear); setReady(id === 'scratch');
  };
  const loadSeed = (payload) => {
    setSeed(payload); setSourceYear(payload.tax_year || null); setParty(partyFromW2(payload));
    setTargetYear(currentYear); setReady(true);
  };
  const carryForward = (saved) => { setSource('upload'); setClient(null); loadSeed(saved.payload || {}); };
  const pickClient = (c) => { setClient(c); setParty(partyFromClient(c.formData)); setReady(true); };
  const openEditor = () => { setEditorKey((k) => k + 1); setEditorOpen(true); };

  return (
    <div className="space-y-6" data-testid="w2-studio">
      <button type="button" onClick={onBack} className="ml-12 md:ml-0 text-zinc-400 hover:text-white text-sm flex items-center gap-2" data-testid="w2studio-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Payday ST
      </button>
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-400" /> Create a W-2</h1>
        <p className="text-sm text-zinc-400 mt-1">Build one from scratch, or carry a prior year forward with the same numbers.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {SOURCES.map((s) => <SourceTile key={s.id} s={s} active={source === s.id} onClick={() => pickSource(s.id)} />)}
      </div>

      {source === 'upload' && !seed && <W2UploadPanel onExtracted={loadSeed} />}
      {source === 'client' && !client && <ClientPicker clients={savedClients} onPick={pickClient} />}
      {!source && <SavedW2List onCarryForward={carryForward} />}

      {ready && (
        <section className="space-y-4" data-testid="w2studio-details">
          {sourceYear && (
            <p className="text-sm text-emerald-300" data-testid="w2studio-source-year">
              Numbers copied exactly from the {sourceYear} W-2. You can add a % raise or recalculate SS / Medicare in the editor.
            </p>
          )}
          <W2PartyForm party={party} setParty={setParty} />
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">New tax year</span>
              <select value={targetYear} onChange={(e) => setTargetYear(Number(e.target.value))} data-testid="w2studio-target-year"
                className="px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-white text-sm focus:outline-none focus:border-emerald-500">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <button type="button" onClick={openEditor} data-testid="w2studio-open-editor"
              className="h-10 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
              Open W-2 editor for {targetYear}
            </button>
          </div>
        </section>
      )}

      <W2CreatorModal
        key={editorKey}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        formData={{ ...(client?.formData || {}), ...party }}
        initialYear={targetYear}
        paystubClientId={client?.id || null}
        initialSavedW2={seed ? { payload: seed } : null}
        sourceYear={sourceYear}
      />
    </div>
  );
}
